import React, { FC, useState } from "react";

// UI components
import { Card } from "@UI/Components/Card/Card";
import { Heading } from "@UI/Components/Heading/Heading";
import { Text } from "@UI/Components/Text/Text";
import { Button } from "@UI/Components/Button/Button";
import { Box } from "@UI/Components/Box/Box";
import { Icon } from "@UI/Components/Icon/Icon";
import { Flex } from "@UI/Components/Flex/Flex";

import { ResponsiveModal } from "@UI/Components/ResponsiveModal/ResponsiveModal";
import { EuroCircle } from "@UI/Components/Icons/EuroCircle";

// i18n
import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";
import messages from "./messages";

// hooks
import useMediaQuery from "@UI/hooks/useMediaQuery";

// Core
import { useNavigate } from "@Libs/Router/hooks/useNavigate";

// routes
import { RouterLink } from "@Libs/Router/Components/RouterLink/RouterLink";
import { ServiceOfferPreview } from "@modules/ServiceAssignment/Components/ServiceOfferPreview/ServiceOfferPreview";

interface Props {
  serviceOfferId: string;
}

export const ServiceFeeFormulaSummary: FC<Props> = ({ serviceOfferId }) => {
  const [showPriceEstimateModal, setShowPriceEstimateModal] = useState(false);
  const navigate = useNavigate();
  const { isMobile } = useMediaQuery();

  const togglePriceEstimateModal = () => {
    setShowPriceEstimateModal(!showPriceEstimateModal);
  };

  const handleSeePriceEstimateClick = (e) => {
    e.preventDefault();
    togglePriceEstimateModal();
  };

  const handleServiceFeeSubscriptionCTAClick = (e) => {
    e.preventDefault();
    navigate(
      ["SERVICE_OFFERS", "SERVICE_OFFER", "ACCEPTANCE", "CHOOSE_FORMULA"],
      {
        params: { serviceOfferId: String(serviceOfferId) },
        queryParams: { anchor: "subscription-formula-card" },
      }
    );
  };

  return (
    <>
      <Card textAlign="start" paddingY={["4", "4", "5"]}>
        <Box>
          <Flex
            alignItems={{ base: "flex-start", md: "center" }}
            justifyContent="space-between"
          >
            <Box>
              <Text color="muted" fontSize="xs" marginBottom="1">
                <FormattedMessage {...messages.yourFormula} />
              </Text>
              <Heading
                fontSize="lg"
                fontFamily="sans-serif"
                marginBottom={["2", "2", "2"]}
              >
                <FormattedMessage {...messages.serviceFeeFormulaShort} />
              </Heading>
              <Button
                fontSize="sm"
                variant="link"
                color="gray.700"
                onClick={handleSeePriceEstimateClick}
                textDecoration="underline"
                paddingX="0"
                paddingY="0"
              >
                <Icon as={EuroCircle} marginRight="1" />
                <FormattedMessage {...messages.viewPriceEstimate} />
              </Button>
            </Box>

            <Button
              as={RouterLink}
              route={[
                "SERVICE_OFFERS",
                "SERVICE_OFFER",
                "ACCEPTANCE",
                "CHOOSE_FORMULA",
              ]}
              params={{
                serviceOfferId: String(serviceOfferId),
              }}
              fontSize={isMobile && "xs"}
              variant={isMobile ? "link" : "outline"}
              colorScheme={isMobile ? "pink" : "seaGreen"}
              textDecoration={isMobile ? "underline" : "initial"}
              paddingX={isMobile ? "0" : "18px"}
              paddingY={isMobile && "0"}
              data-testid="change-formula"
            >
              <FormattedMessage {...messages.change} />
            </Button>
          </Flex>
        </Box>
      </Card>
      <ResponsiveModal
        onClose={togglePriceEstimateModal}
        isOpen={showPriceEstimateModal}
      >
        <Box paddingBottom="4">
          <ServiceOfferPreview
            serviceOfferId={serviceOfferId}
            handleServiceFeeCTAClick={handleServiceFeeSubscriptionCTAClick}
            showServiceFeeCTA
          />
        </Box>
      </ResponsiveModal>
    </>
  );
};
